import {__, sprintf} from '@wordpress/i18n';
import {CheckboxControl, SelectControl} from '@wordpress/components';
import {SetAttributes} from './Edit';
import {Type} from '@wordpress/api/types';
import {useSelect} from '@wordpress/data';
import {decodeEntities} from '@wordpress/html-entities';
import {PostTypeOptions} from './PostType';
import {useEffect, useRef} from 'react';
import {Taxonomy} from '@wordpress/api/taxonomies';
import {CONFIG} from '../../../globals/config';

export type DisplayOptions = {
	display_everywhere: boolean;
	'display_everywhere/parent_page': number;
} & PostTypeOptions;

type Props = {
	attributes: DisplayOptions;
	setAttributes: SetAttributes;
	type?: Type<'edit'> | Taxonomy<'edit'>;
};

const Display = ( {attributes, setAttributes, type}: Props ) => {
	const postType = attributes.post_type ?? 'page';
	const currentPostType = useRef<string>( postType );

	const pages = useSelect( select => {
		const items = select( 'core' ).getEntityRecords( 'postType', postType as 'page', {
			parent: 0,
			orderby: 'title',
			order: 'asc',
			per_page: 100,
		} ) ?? [];
		return items.filter( item => ! CONFIG.pages.excluded.includes( item.id ) );
	}, [ postType ] );

	// Set the parent page to the first available when post type is switched.
	useEffect( () => {
		if ( currentPostType.current === postType ) {
			return;
		}
		currentPostType.current = postType;
		setAttributes( {
			'display_everywhere/parent_page': pages.length > 0 ? pages[ 0 ].id : 0,
		} );
	}, [ postType, setAttributes, pages ] );

	// Set the parent page to the first available page when no page is selected.
	useEffect( () => {
		if ( 0 === attributes[ 'display_everywhere/parent_page' ] && pages.length > 0 ) {
			setAttributes( {
				'display_everywhere/parent_page': pages[ 0 ].id,
			} );
		}
		// eslint-disable-next-line -- Don't want to set if the attribute changes.
	}, [ pages ] );

	return ( <>
		<CheckboxControl
			label={__( 'Display menu on every page', 'advanced-sidebar-menu' )}
			help={__( 'Display the same menu on every page that has this block.', 'advanced-sidebar-menu' )}
			checked={attributes.display_everywhere}
			onChange={value => {
				setAttributes( {
					display_everywhere: value,
				} );
			}}
			// @ts-expect-error -- Not technically available until WP 6.7.
			__nextHasNoMarginBottom
		/>

		{attributes.display_everywhere && <>
			<SelectControl
				value={attributes[ 'display_everywhere/parent_page' ].toString()}
				/* translators: %s post-type singular label */
				label={sprintf( __( 'Highest level parent %s', 'advanced-sidebar-menu' ), type?.labels?.singular_name.toLowerCase() ?? '' )}
				/* translators: %s post-type singular label */
				help={sprintf( __( 'Menu generates using this %s as the top level item.', 'advanced-sidebar-menu' ), type?.labels?.singular_name.toLowerCase() ?? '' )}
				options={pages.map( page => ( {
					label: decodeEntities( page.title.rendered ),
					value: page.id.toString(),
				} ) )}
				onChange={value => {
					setAttributes( {
						'display_everywhere/parent_page': parseInt( value ),
					} );
				}} />
		</>}

	</> );
};

export default Display;
